import {
  SignInType,
  type GetSignInUrlArgs,
  type SignInArgs,
  type SignInTokens,
} from '@bbkAdminRedux/rtkq/auth-types';
import {
  type BbkClientConfig,
  type CognitoClientConfig,
} from '@bbkAdminRedux/rtkq/preauth-company.slice';
import { AuthChargebee } from '@bbkAdminUtils/auth-chargebee';
import { AuthDev } from '@bbkAdminUtils/auth-dev';
import { AuthGoogle } from '@bbkAdminUtils/auth-google';
import { AuthUsername } from '@bbkAdminUtils/auth-username';
import BrightbackSession from '@bbkAdminUtils/brightback-session';
import { GlobalLogger } from '@bbkAdminUtils/GlobalLogger';

export type SignOutArgs = {
  type: SignInType;
  refresh_token?: string;
  redirect: string;
};

export type RefreshTokenArgs = {
  type: SignInType;
  refresh_token: string;
};

const COGNITO_AUTH_CONFIG_ERROR = 'Cognito auth is not configured';

export const hasCognitoConfig = (
  config: BbkClientConfig
): config is CognitoClientConfig => {
  return Boolean(
    config.cognito_user_pool_id &&
    config.cognito_web_client_id &&
    config.cognito_region &&
    config.cognito_domain
  );
};

export class BrightbackAuth {
  private config: BbkClientConfig;

  private devAuth: AuthDev;

  private googleAuth?: AuthGoogle;

  private usernameAuth?: AuthUsername;

  private chargebeeAuth: AuthChargebee;

  constructor(config: BbkClientConfig) {
    this.config = config;
    this.devAuth = new AuthDev();
    this.chargebeeAuth = new AuthChargebee(config);
  }

  private getCognitoConfig(): CognitoClientConfig {
    if (!hasCognitoConfig(this.config)) {
      throw new Error(COGNITO_AUTH_CONFIG_ERROR);
    }

    return this.config;
  }

  private getGoogleAuth() {
    if (!this.googleAuth) {
      this.googleAuth = new AuthGoogle(this.getCognitoConfig());
    }

    return this.googleAuth;
  }

  private getUsernameAuth() {
    if (!this.usernameAuth) {
      this.usernameAuth = new AuthUsername(this.getCognitoConfig());
    }

    return this.usernameAuth;
  }

  public async signOut({
    refresh_token,
    type,
    redirect,
  }: SignOutArgs): Promise<void> {
    const isGoogle = type === SignInType.GOOGLE;
    const isChargebee = type === SignInType.CHARGEBEE;
    const isCognitoUsername = type === SignInType.USERNAME;

    try {
      if (isGoogle || isCognitoUsername) {
        // should be used for all users, not just google
        await this.getGoogleAuth().signOut(refresh_token);
      }

      if (isCognitoUsername) {
        this.getUsernameAuth().signOutUsername();
      }
    } catch (err) {
      GlobalLogger.error(err);
    }

    BrightbackSession.clear();

    if (isChargebee) {
      window.location.assign(AuthChargebee.signOutUrl({ redirect }));
    } else {
      window.location.assign(redirect);
    }
  }

  public async refreshTokens({
    refresh_token,
    type,
  }: RefreshTokenArgs): Promise<SignInTokens> {
    switch (type) {
      case SignInType.DEV:
        throw new Error('DEV auth does not support token refresh');
      case SignInType.GOOGLE:
        return this.getGoogleAuth().doRefresh(refresh_token);
      case SignInType.CHARGEBEE:
        return this.chargebeeAuth.doRefresh(refresh_token);
      case SignInType.USERNAME:
        return this.getUsernameAuth().doRefresh(refresh_token);
      default:
        throw new Error('User is signed in with unsupported type');
    }
  }

  public signIn(opts: SignInArgs): Promise<SignInTokens> {
    switch (opts.type) {
      case SignInType.USERNAME:
        return this.getUsernameAuth().signInUsername(opts);
      case SignInType.DEV:
        return this.devAuth.signIn(opts);
      case SignInType.GOOGLE:
        return this.getGoogleAuth().signIn();
      case SignInType.CHARGEBEE:
        return this.chargebeeAuth.signIn(opts);
      default:
        throw new Error('Invalid sign-in type');
    }
  }

  public getSignInUrl(opts: GetSignInUrlArgs): Promise<string> {
    switch (opts.type) {
      case SignInType.CHARGEBEE: {
        return Promise.resolve(this.chargebeeAuth.getSignInUrl(opts));
      }
      default: {
        throw new Error('Invalid sign-in type');
      }
    }
  }

  public signUp(args: Parameters<AuthUsername['signUp']>[0]) {
    return this.getUsernameAuth().signUp(args);
  }

  public resendSignUpConfirmation(email: string) {
    return this.getUsernameAuth().resendSignUpConfirmation(email);
  }

  public sendUpdatePasswordCode(email: string) {
    return this.getUsernameAuth().sendUpdatePasswordCode(email);
  }

  public updatePassword(
    email: string,
    password: string,
    updatePasswordCode: string
  ) {
    return this.getUsernameAuth().updatePassword(
      email,
      password,
      updatePasswordCode
    );
  }

  public mfaUpdatePassword(
    ...args: Parameters<AuthUsername['mfaUpdatePassword']>
  ) {
    return this.getUsernameAuth().mfaUpdatePassword(...args);
  }

  public confirmSignUp(...args: Parameters<AuthUsername['confirmSignUp']>) {
    return this.getUsernameAuth().confirmSignUp(...args);
  }
}
